import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { Globe } from "lucide-react";
import { z } from "zod";
import { EmptyState } from "@/components/app/empty-state";
import { SiteCard } from "@/components/app/site-card";
import { TerminalBlock } from "@/components/app/terminal-block";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { siteQueries } from "@/lib/queries";
import type { Site } from "@/lib/schemas";

const SORT_OPTIONS = {
  deployed: "Last deployed",
  name: "Alphabetical",
  created: "Created",
} as const;

type SortKey = keyof typeof SORT_OPTIONS;

const sitesSearch = z.object({
  sort: z.enum(["deployed", "name", "created"]).optional(),
});

export const Route = createFileRoute("/_authed/sites/")({
  validateSearch: zodValidator(sitesSearch),
  loader: ({ context }) => context.queryClient.ensureQueryData(siteQueries.list()),
  component: SitesIndex,
});

function sortSites(sites: Site[], sort: SortKey): Site[] {
  const sorted = [...sites];
  switch (sort) {
    case "name":
      sorted.sort((a, b) => a.name.localeCompare(b.name));
      break;
    case "created":
      // Newest first.
      sorted.sort((a, b) => b.created_at.localeCompare(a.created_at));
      break;
    case "deployed":
      // Most recently deployed first; sites with no active deploy sink to
      // the bottom (ordered amongst themselves by created_at desc).
      sorted.sort((a, b) => {
        const aAt = a.active_deploy?.created_at;
        const bAt = b.active_deploy?.created_at;
        if (aAt && bAt) return bAt.localeCompare(aAt);
        if (aAt) return -1;
        if (bAt) return 1;
        return b.created_at.localeCompare(a.created_at);
      });
      break;
  }
  return sorted;
}

function SitesIndex() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { data } = useSuspenseQuery(siteQueries.list());
  const sort: SortKey = search.sort ?? "deployed";
  const sites = sortSites(data.data, sort);

  return (
    <section>
      <header className="mb-8 flex items-center justify-between gap-4">
        <h1 className="font-display text-2xl font-semibold text-foreground">Sites</h1>
        {sites.length > 1 && (
          <Select
            value={sort}
            onValueChange={(value) =>
              navigate({
                search: { sort: value === "deployed" ? undefined : (value as SortKey) },
                replace: true,
              })
            }
          >
            <SelectTrigger className="w-[180px]" aria-label="Sort sites">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(Object.entries(SORT_OPTIONS) as [SortKey, string][]).map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </header>

      {sites.length === 0 ? (
        <EmptyState
          title="No sites yet"
          description="Deploy your first site from the CLI."
          icon={Globe}
        >
          <TerminalBlock
            lines={[
              { text: "brew install sandbar-cloud/tap/sandbar" },
              { text: "cd my-site" },
              { text: "sandbar init" },
              { text: "sandbar deploy", highlight: true },
            ]}
          />
        </EmptyState>
      ) : (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {sites.map((s) => (
            <SiteCard
              key={s.slug}
              slug={s.slug}
              name={s.name}
              liveUrl={`https://${s.slug}.on.sandbar.cloud`}
              thumbnailUrl={s.thumbnail_url}
              hasActiveDeploy={!!s.active_deploy_id}
              deployMessage={s.active_deploy?.message}
              lastDeployedAt={s.active_deploy?.created_at}
              sizeBytes={s.active_deploy?.total_bytes}
            />
          ))}
        </div>
      )}
    </section>
  );
}
